import React, { useState, useRef, useEffect } from 'react';
import { Camera, Loader2, Upload, X, Check } from '@/components/icons';
import { uploadProfileImage } from '../../services/imageUploadService';
import { useAuth } from '../../pages/Auth/AuthProvider';
import { resolveAssetUrl } from '../../lib/assets';

interface ProfilePictureUploadProps {
  currentImage?: string | null;
  userName?: string;
  onImageUpdate?: (imageUrl: string) => void;
  size?: 'sm' | 'md' | 'lg';
  showButton?: boolean;
}

export function ProfilePictureUpload({ 
  currentImage, 
  userName, 
  onImageUpdate,
  size = 'md',
  showButton = true 
}: ProfilePictureUploadProps) {
  const { user, updateUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear cache-busting after 1 hour to match backend cache
  useEffect(() => {
    if (newImageUrl) {
      const timer = setTimeout(() => {
        setNewImageUrl(null);
      }, 3600000); // 1 hour in milliseconds
      
      return () => clearTimeout(timer);
    }
  }, [newImageUrl]);

  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-20 h-20', 
    lg: 'w-32 h-32'
  };

  const buttonSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    setSuccess(false);
    setIsLoading(true);
    setIsUploading(true);

    try {
      // Step 1: Upload image to R2
      const uploadResult = await uploadProfileImage(file);
      
      if (!uploadResult.success) {
        setError(uploadResult.error || 'Upload failed');
        return;
      }

      // Step 2: Update user profile in database and refresh session.
      // Store the R2 object key (not the absolute URL) so future public-domain
      // changes are pure env-var changes; the server normalizes either way.
      const storedImage = uploadResult.key || uploadResult.imageUrl!;
      setIsUploading(false);
      if (user && updateUser) {
        await updateUser({ ...user, image: storedImage });
        // Store the resolved URL for cache-busting
        setNewImageUrl(uploadResult.imageUrl!);
      }

      // Step 3: Notify parent component
      onImageUpdate?.(storedImage);

      // Step 4: Show success message
      setSuccess(true);

      // Step 5: Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);

    } catch (err) {
      console.error('Upload error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
      setIsUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayImage = resolveAssetUrl(currentImage || user?.image);
  const displayName = userName || user?.name;

  // Use cache-busting only for newly uploaded images (first hour)
  const isRecentlyUploaded = newImageUrl === displayImage;
  const uploadTime = isRecentlyUploaded ? Date.now() : null;
  const imageUrl = displayImage && isRecentlyUploaded ? `${displayImage}?t=${uploadTime}` : displayImage;

  return (
    <div className="relative inline-block">
      {/* Profile Picture */}
      <div className={`${sizeClasses[size]} relative group cursor-pointer`} onClick={handleClick}>
        {displayImage ? (
          <img
            key={imageUrl || undefined} // Force re-render when URL changes
            src={imageUrl || undefined}
            alt={displayName || 'Profile'}
            className={`w-full h-full rounded-full object-cover border-2 border-border dark:border-border`}
          />
        ) : (
          <div className={`w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-medium border-2 border-border dark:border-border ${
            size === 'sm' ? 'text-sm' : size === 'md' ? 'text-lg' : 'text-2xl'
          }`}>
            {displayName?.charAt(0).toUpperCase() || 'U'}
          </div>
        )}

        {/* Upload Overlay */}
        <div className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {isLoading ? (
            <Loader2 className={`${buttonSizeClasses[size]} text-white animate-spin`} />
          ) : (
            <Camera className={`${buttonSizeClasses[size]} text-white`} />
          )}
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload Button (optional) */}
      {showButton && (
        <button
          onClick={handleClick}
          disabled={isLoading}
          className="mt-2 flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-muted text-foreground hover:bg-muted dark:bg-card dark:text-foreground dark:hover:bg-muted disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isUploading ? 'Uploading...' : 'Updating...'}
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              Change Photo
            </>
          )}
        </button>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <X className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="mt-2 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <Check className="w-4 h-4" />
          Profile picture updated successfully!
        </div>
      )}
    </div>
  );
}
